// Shared brand-logo resolver used by every unauthenticated / auth
// page (Login, ForgotPassword, ResetPassword, invitation flows).
//
// Why this exists
// ---------------
// The public /api/company/branding endpoint returns two logo URLs:
//   - branding.logo       — the primary company logo (also used by
//                            the sidebar in Menu.tsx)
//   - branding.login_logo — an optional login-only variant. Backend
//                            falls back to `companyLogo` when it's
//                            not explicitly configured, so under
//                            normal conditions both are identical.
//
// A previous tenant seed populated `login_logo_path` with a small
// auto-generated initial-avatar image (a purple "P" square) so the
// login page rendered that instead of the real Step Up Academy
// horizontal logo. The sidebar was correct because it reads
// `branding.logo` (which is not overridden by that bad path).
//
// The naive resolver `loginLogo || logo || shipped` picks up the
// bad URL because "" isn't returned (the auto-avatar URL is a
// truthy string). `onError` cannot help because the URL loads with
// a 200 OK — the response body just isn't the real logo.
//
// This helper adds a defensive `isPlaceholderLogo(url)` check so a
// clearly-placeholder value falls through to the next tier without
// changing the user-facing precedence spec:
//
//   1. tenant loginLogo, if not a placeholder pattern
//   2. tenant primary logo, if not a placeholder pattern
//   3. shipped Step Up Academy asset (public/Images/stepup-logo.webp)
//
// The helper never throws and never returns an empty string — the
// shipped asset is the final fallback so a broken tenant record can
// never render a blank/broken image icon on the login card.

const PLACEHOLDER_PATTERNS: RegExp[] = [
  // Common auto-generated initial-avatar services.
  /\bui-avatars\.com\b/i,
  /\bavatars?\.dicebear\.com\b/i,
  /\bgravatar\.com\/avatar\b/i,
  // Explicit "no logo" / "placeholder" filenames.
  /\/(placeholder|default[-_]?logo|no[-_]?logo|no[-_]?image|dummy[-_]?logo)\.[a-z0-9]+(?:$|\?)/i,
  // 1- or 2-character filenames like `/p.png` or `/AB.jpg` —
  // characteristic of initial-avatar generators that write a single
  // letter of the tenant's name into a coloured square.
  /\/[A-Za-z0-9]{1,2}\.(?:png|jpe?g|webp|gif|svg)(?:$|\?)/,
  // Query-string generators that literally spell out the initial.
  /[?&](?:name|initials|text|letter)=[A-Za-z0-9]{1,2}(?:$|&)/i,
];

/**
 * Returns true if `url` looks like an auto-generated placeholder /
 * initial-avatar rather than a real company logo. Empty / null /
 * undefined all count as "placeholder" so callers can rely on a
 * single truthy check.
 */
export function isPlaceholderLogo(url: string | null | undefined): boolean {
  if (!url) return true;
  const s = String(url).trim();
  if (s === "") return true;
  for (const rx of PLACEHOLDER_PATTERNS) {
    if (rx.test(s)) return true;
  }
  return false;
}

/**
 * The shipped Step Up Academy default logo. Lives in the CRA public
 * folder so it works at every deployed origin (dev, staging, prod)
 * without an environment-dependent base URL.
 */
export const SHIPPED_STEPUP_LOGO = `${process.env.PUBLIC_URL || ""}/Images/stepup-logo.webp`;

/**
 * Auth-page logo resolver.
 *
 * Precedence (spec §"Prefer tenant branding when configured"):
 *   1. loginLogo — tenant's login-specific upload, if not a placeholder
 *   2. logo      — tenant's primary logo (matches sidebar identity)
 *   3. SHIPPED_STEPUP_LOGO — Step Up Academy default asset
 *
 * `null` / empty / placeholder-pattern values are transparently
 * skipped at each tier so a bad tenant seed can never surface as
 * an initial-avatar square on the login card.
 */
export function resolveAuthLogo(
  loginLogo: string | null | undefined,
  logo: string | null | undefined,
): string {
  if (!isPlaceholderLogo(loginLogo)) return String(loginLogo).trim();
  if (!isPlaceholderLogo(logo))       return String(logo).trim();
  return SHIPPED_STEPUP_LOGO;
}

/**
 * onError swap. Always safe to call — no-ops when the current src
 * is already the shipped fallback (prevents an infinite onError loop
 * on environments where even the shipped path 404s).
 */
export function swapToShippedLogo(el: HTMLImageElement): void {
  if (el.src === SHIPPED_STEPUP_LOGO) return;
  if (el.src.endsWith("/Images/stepup-logo.webp")) return;
  el.src = SHIPPED_STEPUP_LOGO;
}
